import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Search, Plus, Minus, Trash2, CreditCard, DollarSign, HandCoins, Calculator, Scan, Percent, Gift } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

import API from '../api_config';
import { POSSessionManager } from '../components/POSSessionManager';

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function POSPage() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [loading, setLoading] = useState(false);
  
  const [openManual, setOpenManual] = useState(false);
  const [manualDetails, setManualDetails] = useState({ name: 'Varios', price: '', quantity: '1' });
  const [openScanner, setOpenScanner] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  
  // Split Payment State
  const [openSplitPayment, setOpenSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);
  const [currentSplitMethod, setCurrentSplitMethod] = useState('efectivo');
  const [currentSplitAmount, setCurrentSplitAmount] = useState('');
  const [documentType, setDocumentType] = useState('boleta'); // 'boleta' or 'factura'

  const { user, socket } = useAuth();

  const addToCart = useCallback((product) => {
    const productData = products.find(p => p.id === product.id) || product;
    const availableStock = productData.stock || 0;
    
    const existing = cart.find(item => item.product_id === product.id);
    const newQty = (existing ? existing.quantity : 0) + 1;
    
    if (newQty > availableStock) {
      toast.error(`Stock insuficiente. Disponible: ${availableStock}`);
      return;
    }

    if (existing) {
      setCart(prev => prev.map(item =>
        item.product_id === product.id
          ? { 
              ...item, 
              quantity: item.quantity + 1, 
              subtotal: (item.quantity + 1) * item.original_price
            }
          : item
      ));
    } else {
      setCart(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price,
        original_price: product.price,
        subtotal: product.price,
        is_manual: false
      }]);
    }
    toast.success(`${product.name} agregado`);
  }, [products, cart]);

  const handleScan = useCallback(async (code) => {
    try {
        const response = await axios.get(`${API}/products/barcode/${code}`);
        addToCart(response.data);
        toast.success(`Producto escaneado: ${response.data.name}`);
        setOpenScanner(false); 
    } catch (error) {
        toast.error('Producto no encontrado');
    }
  }, [addToCart]);



  /* 
   * Fetch products with stock for a specific warehouse.
   * If warehouseId is provided, backend filters/calculates stock for that warehouse.
   */
  const fetchProducts = useCallback(async (warehouseId) => {
    try {
      const params = {};
      if (warehouseId) params.warehouse_id = warehouseId;

      const response = await axios.get(`${API}/products`, { params });
      // Ensure products have price as number
      const mapped = response.data.map(p => ({...p, price: Number(p.price)}));
      setProducts(mapped);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  }, []);
  
  const fetchPromotions = useCallback(async () => {
    try {
        const response = await axios.get(`${API}/promotions/active`);
        setPromotions(response.data);
    } catch (error) {
        console.error("Error fetching promotions", error);
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warehouses`);
      setWarehouses(response.data);
      if (response.data.length > 0) {
        setSelectedWarehouse(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    // Initial fetch of metadata
    fetchWarehouses();
    fetchCustomers();
    fetchSettings();
    fetchPromotions();
  }, [fetchWarehouses, fetchCustomers, fetchSettings, fetchPromotions]);

  // When session changes or selectedWarehouse changes, fetch products
  useEffect(() => {
     // If session is active, use session's warehouse. 
     // Fallback to selectedWarehouse (for viewing without session? mostly for debugging or setup)
     const targetWarehouse = activeSession?.warehouse_id || selectedWarehouse;
     
     if (targetWarehouse) {
         fetchProducts(targetWarehouse);
     } else {
         fetchProducts(); // Fetch all/default
     }
  }, [activeSession, selectedWarehouse, fetchProducts]);

  // Removed the old fetchInventoryForWarehouse logic as it is now integrated into fetchProducts
  // and triggered by the useEffect above.

  const handleBarcodeInput = useCallback(async (e) => {
    const value = e.target.value;
    setBarcodeInput(value);
    
    if (e.key === 'Enter' && value.trim()) {
      try {
        const response = await axios.get(`${API}/products/barcode/${value.trim()}`);
        addToCart(response.data);
        setBarcodeInput('');
        toast.success('Producto escaneado agregado');
      } catch (error) {
        toast.error('Producto no encontrado con ese código de barras');
        setBarcodeInput('');
      }
    }
  }, [addToCart]);

  const addManualProduct = useCallback(() => {
      const price = parseFloat(manualDetails.price);
      const qty = parseFloat(manualDetails.quantity);
      
      if (!price || price <= 0) {
          toast.error('Ingrese un precio válido');
          return;
      }

      // Generate a temporary ID for cart logic
      const tempId = `manual-${Date.now()}`;
      
      const product = {
          id: tempId,
          name: manualDetails.name || 'Varios',
          price: price,
          is_manual: true
      };
      
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        quantity: qty || 1,
        price: product.price,
        original_price: product.price,
        subtotal: product.price * (qty || 1),
        is_manual: true
      };
      
      setCart(prev => [...prev, newItem]);
      setOpenManual(false);
      setManualDetails({ name: 'Varios', price: '', quantity: '1' });
      toast.success('Producto manual agregado');
  }, [manualDetails]);


  const setManualQuantity = useCallback((productId, qty) => {
    const product = products.find(p => p.id === productId);
    const newQty = parseFloat(qty) || 1;
    
    if (product && newQty > (product.stock || 0)) {
       toast.error(`Stock insuficiente. Disponible: ${product.stock || 0}`);
       return;
    }

    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        return {
          ...item,
          quantity: newQty,
          subtotal: newQty * item.original_price
        };
      }
      return item;
    }));
  }, [products]);

  const updateQuantity = useCallback((productId, delta) => {
    if (delta > 0) {
      const product = products.find(p => p.id === productId);
      const item = cart.find(i => i.product_id === productId);
      
      if (product && item) {
         if (item.quantity + delta > (product.stock || 0)) {
           toast.error(`Stock insuficiente. Disponible: ${product.stock || 0}`);
           return;
         }
      }
    }

    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQuantity,
          subtotal: newQuantity * item.original_price
        };
      }
      return item;
    }));
  }, [products, cart]);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  }, []);

  // --- CORE PROMOTION LOGIC ---
  const calculatedData = useMemo(() => {
      // 1. Calculate Base Subtotal (Sum of all items at original price)
      const baseSubtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
      
      // 2. Calculate Discounts
      let tempCart = JSON.parse(JSON.stringify(cart)); // Deep copy to simulate consumption
      let discountDetails = [];
      let totalDiscount = 0;

      // Filter active promotions
      const activePromos = promotions.filter(p => p.is_active);
      const combos = activePromos.filter(p => p.promotion_type === 'combo');
      const simplePromos = activePromos.filter(p => p.promotion_type === 'simple' || !p.promotion_type); // Backward compat

      // Sort combos greedy (by num requirements desc)
      combos.sort((a, b) => (b.requirements?.length || 0) - (a.requirements?.length || 0));

      // A. Process Combos
      for (const combo of combos) {
          if(!combo.requirements) continue;
          
          while (true) {
              // Check if fits
              let fits = true;
              for (const req of combo.requirements) {
                  const item = tempCart.find(i => i.product_id === req.product_id);
                  if (!item || item.quantity < req.quantity) {
                      fits = false;
                      break;
                  }
              }

              if (fits) {
                  // Apply
                  let comboOriginalPrice = 0;
                  for (const req of combo.requirements) {
                      const item = tempCart.find(i => i.product_id === req.product_id);
                      item.quantity -= req.quantity;
                      comboOriginalPrice += (item.original_price * req.quantity);
                  }
                  
                  const savings = comboOriginalPrice - combo.promo_price;
                  if (savings > 0) {
                      const existingDetail = discountDetails.find(d => d.id === combo.id);
                      if (existingDetail) {
                          existingDetail.count++;
                          existingDetail.savings += savings;
                      } else {
                          discountDetails.push({ id: combo.id, name: combo.name, count: 1, savings });
                      }
                      totalDiscount += savings;
                  }
              } else {
                  break;
              }
          }
      }

      // B. Process Simple Promos (Volume)
      // Sort by min_quantity desc
      simplePromos.sort((a, b) => b.min_quantity - a.min_quantity);

      for (const item of tempCart) {
          if (item.quantity <= 0) continue;

          const promo = simplePromos.find(p => p.product_id === item.product_id && item.quantity >= p.min_quantity);
          if (promo) {
              const originalLineTotal = item.quantity * item.original_price;
              const promoLineTotal = item.quantity * promo.promo_price;
              const savings = originalLineTotal - promoLineTotal;

              if (savings > 0) {
                  discountDetails.push({ 
                      id: promo.id, 
                      name: `Oferta ${item.product_name}`, 
                      count: 1, 
                      savings 
                  });
                  totalDiscount += savings;
              }
              // Item fully consumed by this volume price rule
              item.quantity = 0;
          }
      }

      // 3. Final Totals
      // Check tax settings
      const taxEnabled = settings?.tax_enabled !== false;
      const taxRate = settings?.tax_rate || 0.19;
      
      const subtotalAfterDiscount = baseSubtotal - totalDiscount;
      const tax = taxEnabled ? (subtotalAfterDiscount * taxRate) : 0;
      
      let fiado_surcharge = 0;
      if (paymentMethod === 'fiado' && settings?.credit_surcharge_percentage) {
        fiado_surcharge = (subtotalAfterDiscount + tax) * (settings.credit_surcharge_percentage / 100);
      }
      
      const total = subtotalAfterDiscount + tax + fiado_surcharge;

      return {
          baseSubtotal,
          discountDetails,
          totalDiscount,
          tax,
          fiado_surcharge,
          total
      };

  }, [cart, promotions, settings, paymentMethod]);


  // --- SPLIT PAYMENT LOGIC ---
  const remainingTotal = useMemo(() => {
    const totalPaid = splitPayments.reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, calculatedData.total - totalPaid);
  }, [calculatedData.total, splitPayments]);

  const addSplitPayment = () => {
    const amount = parseFloat(currentSplitAmount);
    if (!amount || amount <= 0) {
      toast.error('Monto inválido');
      return;
    }
    
    if (amount > remainingTotal + 0.1) { // Tolerance
       toast.error(`El monto excede el total restante (${formatCLP(remainingTotal)})`);
       return;
    }

    setSplitPayments([...splitPayments, { method: currentSplitMethod, amount }]);
    setCurrentSplitAmount('');
    
    // Auto-select next logical step or reset
    if (remainingTotal - amount <= 5) { // Basically 0
        // Done
    } else {
        // Prepare for next entry
        setCurrentSplitMethod('efectivo'); 
        setCurrentSplitAmount((remainingTotal - amount).toString());
    }
  };

  const removeSplitPayment = (index) => {
    const newPayments = [...splitPayments];
    newPayments.splice(index, 1);
    setSplitPayments(newPayments);
  };
  
  const openSplitModal = useCallback(() => {
      setSplitPayments([]);
      setCurrentSplitMethod('efectivo');
      setCurrentSplitAmount(calculatedData.total.toString());
      setOpenSplitPayment(true);
      setPaymentMethod('multiple');
  }, [calculatedData.total]);




  const handleCheckout = useCallback(async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    if (!selectedWarehouse) {
      toast.error('Selecciona una bodega');
      return;
    }

    if (paymentMethod === 'fiado' && !selectedCustomer) {
      toast.error('Debes seleccionar un cliente para pago fiado');
      return;
    }

    if (documentType === 'factura') {
      if (!selectedCustomer) {
        toast.error('Debes seleccionar un cliente para emitir Factura');
        return;
      }
      const customer = customers.find(c => c.id === selectedCustomer);
      if (!customer) return; // Should not happen
      
      const missing = [];
      if (!customer.rut) missing.push('RUT');
      if (!customer.giro) missing.push('Giro');
      if (!customer.address) missing.push('Dirección');
      
      if (missing.length > 0) {
        toast.error(`Datos faltantes para Factura: ${missing.join(', ')}`);
        // Optionally suggest editing customer here
        return;
      }
    }

    setLoading(true);
    try {
      if (paymentMethod === 'multiple') {
          // Validate sum
          const totalPaid = splitPayments.reduce((sum, p) => sum + p.amount, 0);
          if (Math.abs(totalPaid - calculatedData.total) > 10) { // Safety margin
            toast.error(`La suma de pagos (${formatCLP(totalPaid)}) no coincide con el total (${formatCLP(calculatedData.total)})`);
            setLoading(false);
            return;
          }
      }

      await axios.post(`${API}/sales`, {
        customer_id: selectedCustomer || null,
        warehouse_id: selectedWarehouse,
        items: cart, // We send items as is. Backend calculates raw subtotal from this.
        payment_method: paymentMethod,
        document_type: documentType, // Included
        payments: paymentMethod === 'multiple' ? splitPayments : null, // Include detailed list
        total: calculatedData.total,
        fiado_surcharge: calculatedData.fiado_surcharge,
        discount_total: calculatedData.totalDiscount,
        discount_details: calculatedData.discountDetails
      });

      toast.success('¡Venta completada!');
      setCart([]);
      setSelectedCustomer('');
      setPaymentMethod('efectivo');
      setOpenSplitPayment(false); // Close modal if open
    } catch (error) {
      toast.error('Error al procesar venta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [cart, selectedWarehouse, paymentMethod, selectedCustomer, documentType, customers, splitPayments, calculatedData]);

  const filteredProducts = products
    .filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (socket) {
      const handleMessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'INVENTORY_UPDATE') {
             setProducts(prev => prev.map(p => 
               p.id === data.product_id ? { ...p, stock: data.new_quantity } : p
             ));
             toast.info(`Actualización de stock: ${data.product_id}`);
          }
        } catch (e) {}
      };
      
      socket.addEventListener('message', handleMessage);
      return () => {
        socket.removeEventListener('message', handleMessage);
      };
    }
  }, [socket]);

  const handleSessionActive = (session) => {
    setIsSessionActive(!!session);
    setActiveSession(session);
    if (session && session.warehouse_id) {
      setSelectedWarehouse(session.warehouse_id);
    }
  };

  return (
    <div className="space-y-4" data-testid="pos-page">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-4xl font-bold tracking-tight">Punto de Venta</h1>
        <div className="flex space-x-2 items-center">
            {socket && <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" title="Conectado en tiempo real"></div>}
            {warehouses.length > 0 && (
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              disabled={isSessionActive}
              className="px-4 py-2 rounded-lg border border-input bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="warehouse-select"
            >
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      
      <POSSessionManager 
        onSessionActive={handleSessionActive} 
        selectedWarehouse={selectedWarehouse} 
        warehouses={warehouses}
      />

      {!isSessionActive ? (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-700">
          <div className="h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mb-4 dark:bg-orange-900/30">
            <span className="text-3xl">🔒</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Caja Cerrada</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
            Debe abrir una caja (iniciar turno) para comenzar a realizar ventas. Seleccione una caja arriba y establezca el monto de apertura.
          </p>
        </div>
      ) : (
        <>

      <Dialog open={openManual} onOpenChange={setOpenManual}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Ingreso Manual de Producto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>Descripción / Nombre</Label>
                    <Input 
                        value={manualDetails.name}
                        onChange={(e) => setManualDetails({...manualDetails, name: e.target.value})}
                        placeholder="Ej. Pan, Varios..."
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                        <Label>Precio Unitario ($)</Label>
                        <Input 
                            type="number"
                            value={manualDetails.price}
                            onChange={(e) => setManualDetails({...manualDetails, price: e.target.value})}
                            placeholder="0"
                        />
                    </div>
                     <div className="space-y-2">
                        <Label>Cantidad / Peso</Label>
                        <Input 
                            type="number"
                            step="0.001"
                            value={manualDetails.quantity}
                            onChange={(e) => setManualDetails({...manualDetails, quantity: e.target.value})}
                            placeholder="1"
                        />
                    </div>
                </div>
                 <div className="bg-muted p-3 rounded-md text-sm text-center">
                    Total: {formatCLP( (parseFloat(manualDetails.price) || 0) * (parseFloat(manualDetails.quantity) || 1) )}
                </div>
                 <Button onClick={addManualProduct} className="w-full">
                    Agregar al Carrito
                </Button>
            </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={openScanner} onOpenChange={setOpenScanner}>
         <DialogContent className="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Escanear Producto</DialogTitle>
                <DialogDescription>Apunta la cámara al código de barras para agregarlo al carrito.</DialogDescription>
            </DialogHeader>
            <BarcodeScanner onScan={handleScan} onClose={() => setOpenScanner(false)} />
         </DialogContent>
      </Dialog>

      <div className="pos-grid">
        <div className="space-y-4">
          <div className="relative bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <div className="flex justify-between items-center mb-2">
                <Label className="text-xs font-semibold block">Escanear Código de Barras</Label>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-indigo-600 hover:text-indigo-800"
                    onClick={() => setOpenScanner(true)}
                >
                    <Scan className="h-4 w-4 mr-1" />
                    Usar Cámara
                </Button>
            </div>
            <Input
              type="text"
              placeholder="Escanee o escriba el código de barras..."
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyPress={handleBarcodeInput}
              className="h-10 font-mono"
              data-testid="barcode-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use la pistola lectora o escriba el código y presione Enter
            </p>
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                type="text"
                placeholder="Buscar productos por nombre o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12"
                data-testid="product-search-input"
                />
            </div>
            <Button 
                variant="outline" 
                className="h-12 w-12 p-0" 
                onClick={() => setOpenManual(true)}
                title="Ingreso Manual / Granel"
            >
                <Calculator className="h-6 w-6" />
            </Button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-h-[calc(100vh-250px)] overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                No se encontraron productos
              </div>
            ) : (
              filteredProducts.map(product => (
                <Card
                  key={product.id}
                  className="product-card cursor-pointer relative"
                  onClick={() => addToCart(product)}
                  data-testid={`product-card-${product.id}`}
                >
                  <CardContent className="p-4 space-y-2">
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      product.stock <= 0 
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
                        : product.stock <= product.min_stock 
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      Stock: {product.stock}
                    </div>
                    
                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-sm line-clamp-2">{product.name}</h3>
                      <p className="text-xs text-muted-foreground font-mono">{product.sku}</p>
                    </div>
                    <p className="font-mono font-bold text-lg text-indigo-600">
                      {formatCLP(product.price)}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        <Card className="h-fit sticky top-4" data-testid="cart-panel">
          <CardHeader className="border-b border-border py-3">
            <CardTitle className="text-xl">Carrito</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-4">
              {paymentMethod === 'fiado' && (
                <div className="space-y-2 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                  <Label className="text-sm font-semibold">Cliente (Requerido para Fiado)</Label>
                  <select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    className="w-full h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    data-testid="customer-select"
                  >
                    <option value="">Seleccionar cliente...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.rut ? `(${c.rut})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
                  <Label className="text-sm font-semibold">Tipo de Documento</Label>
                  <div className="flex space-x-2">
                       <Button 
                          variant={documentType === 'boleta' ? 'default' : 'outline'}
                          onClick={() => setDocumentType('boleta')}
                          className="flex-1"
                       >
                           Boleta
                       </Button>
                       <Button 
                          variant={documentType === 'factura' ? 'default' : 'outline'}
                          onClick={() => setDocumentType('factura')}
                          className="flex-1"
                       >
                           Factura
                       </Button>
                  </div>
              </div>

               {/* Force customer selection if Factura is selected, even if not fiado */}
              {documentType === 'factura' && paymentMethod !== 'fiado' && (
                 <div className="space-y-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Label className="text-sm font-semibold">Cliente (Requerido para Factura)</Label>
                  <select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    className="w-full h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    data-testid="customer-select-factura"
                  >
                    <option value="">Seleccionar cliente...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.rut ? `(${c.rut})` : ''}
                      </option>
                    ))}
                  </select>
                   {selectedCustomer && (() => {
                      const c = customers.find(cust => cust.id === selectedCustomer);
                      if(c && (!c.rut || !c.giro || !c.address)) {
                          return (
                              <p className="text-xs text-red-500 font-bold mt-1">
                                  ⚠️ Faltan datos tributarios (RUT, Giro, Dirección)
                              </p>
                          );
                      }
                      return null;
                  })()}
                </div>
              )}

              <div className="max-h-[250px] overflow-y-auto space-y-2">
                <AnimatePresence>
                  {cart.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8 text-sm">Carrito vacío</p>
                  ) : (
                    cart.map(item => (
                      <motion.div
                        key={item.product_id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="flex items-center justify-between p-3 rounded-lg border border-border bg-card"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.product_name}</p>
                          <p className="text-xs font-mono text-muted-foreground">{formatCLP(item.original_price)} x {item.quantity}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product_id, -1)} className="h-7 w-7 p-0" data-testid={`decrease-qty-${item.product_id}`}><Minus className="h-3 w-3" /></Button>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => setManualQuantity(item.product_id, e.target.value)}
                            className="h-7 w-16 px-1 text-center font-mono font-bold text-sm"
                            data-testid={`qty-input-${item.product_id}`}
                          />
                          <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product_id, 1)} className="h-7 w-7 p-0" data-testid={`increase-qty-${item.product_id}`}><Plus className="h-3 w-3" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => removeFromCart(item.product_id)} className="h-7 w-7 p-0 ml-1" data-testid={`remove-item-${item.product_id}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>

              {/* SUMMARY */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-mono font-medium">{formatCLP(calculatedData.baseSubtotal)}</span>
                </div>
                
                {/* DISCOUNTS SECTION */}
                {calculatedData.discountDetails.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded-lg space-y-1">
                        <div className="flex items-center text-green-700 dark:text-green-400 text-xs font-bold mb-1">
                             <Gift className="h-3 w-3 mr-1" /> Ofertas Aplicadas
                        </div>
                        {calculatedData.discountDetails.map((disc, idx) => (
                            <div key={idx} className="flex justify-between text-xs text-green-600 dark:text-green-300">
                                <span>{disc.count > 1 ? `(${disc.count}) ` : ''}{disc.name}</span>
                                <span>-{formatCLP(disc.savings)}</span>
                            </div>
                        ))}
                         <div className="flex justify-between text-sm font-bold text-green-700 dark:text-green-400 border-t border-green-200 mt-1 pt-1">
                            <span>Ahorro Total:</span>
                            <span>-{formatCLP(calculatedData.totalDiscount)}</span>
                        </div>
                    </div>
                )}

                {settings?.tax_enabled !== false && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">IVA ({Math.round((settings?.tax_rate || 0.19) * 100)}%):</span>
                    <span className="font-mono font-medium">{formatCLP(calculatedData.tax)}</span>
                  </div>
                )}
                {calculatedData.fiado_surcharge > 0 && (
                  <div className="flex justify-between text-sm text-orange-600 dark:text-orange-400">
                    <span>Recargo Fiado ({settings?.credit_surcharge_percentage}%):</span>
                    <span className="font-mono font-medium">{formatCLP(calculatedData.fiado_surcharge)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold border-t border-border pt-2">
                  <span>Total:</span>
                  <span className="font-mono text-indigo-600">{formatCLP(calculatedData.total)}</span>
                </div>
              </div>

              {/* SPLIT PAYMENT MODAL */}
              <Dialog open={openSplitPayment} onOpenChange={setOpenSplitPayment}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Pago Mixto / Dividido</DialogTitle>
                    <DialogDescription>
                      Total a Pagar: {formatCLP(calculatedData.total)}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg flex justify-between items-center">
                       <span className="font-semibold">Restante:</span>
                       <span className={`text-xl font-bold font-mono ${remainingTotal > 0 ? 'text-red-500' : 'text-green-600'}`}>
                         {formatCLP(remainingTotal)}
                       </span>
                    </div>

                    <div className="flex gap-2">
                       <select 
                         value={currentSplitMethod}
                         onChange={(e) => setCurrentSplitMethod(e.target.value)}
                         className="flex-1 rounded-md border border-input px-3 py-2 bg-background text-sm"
                       >
                         <option value="efectivo">Efectivo</option>
                         <option value="debito">Tarjeta de Débito</option>
                         <option value="credito">Tarjeta de Crédito</option>
                         <option value="transferencia">Transferencia</option>
                         <option value="fiado">Crédito</option>
                       </select>
                       <Input
                         type="number"
                         value={currentSplitAmount}
                         onChange={(e) => setCurrentSplitAmount(e.target.value)}
                         placeholder="Monto"
                         className="w-32"
                       />
                       <Button onClick={addSplitPayment} size="icon" variant="outline" className="shrink-0">
                         <Plus className="h-4 w-4" />
                       </Button>
                    </div>

                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {splitPayments.length === 0 && <p className="text-center text-sm text-muted-foreground py-2">Agregue métodos de pago</p>}
                      {splitPayments.map((p, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 border rounded bg-card text-sm">
                           <div className="flex gap-2 capitalize">
                              <span className="font-medium">{p.method}</span>
                           </div>
                           <div className="flex items-center gap-2">
                              <span className="font-mono">{formatCLP(p.amount)}</span>
                              <Button variant="ghost" size="sm" onClick={() => removeSplitPayment(idx)} className="h-6 w-6 p-0 text-red-500">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                           </div>
                        </div>
                      ))}
                    </div>

                    <Button 
                      className="w-full" 
                      disabled={remainingTotal > 10} // Tolerance
                      onClick={handleCheckout}
                    >
                      Confirmar Pago Mixto
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={paymentMethod === 'efectivo' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('efectivo')}
                    className="h-20 flex flex-col items-center justify-center"
                    data-testid="payment-efectivo-btn"
                  >
                    <DollarSign className="h-6 w-6 mb-1" />
                    <span className="text-xs">Efectivo</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'debito' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('debito')}
                    className="h-20 flex flex-col items-center justify-center"
                    data-testid="payment-debito-btn"
                  >
                    <CreditCard className="h-6 w-6 mb-1" />
                    <span className="text-xs text-center">Tarjeta de Débito</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'credito' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('credito')}
                    className="h-20 flex flex-col items-center justify-center"
                    data-testid="payment-credito-btn"
                  >
                    <CreditCard className="h-6 w-6 mb-1" />
                    <span className="text-xs">Tarjeta de Crédito</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'transferencia' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('transferencia')}
                    className="h-20 flex flex-col items-center justify-center"
                    data-testid="payment-transferencia-btn"
                  >
                    <CreditCard className="h-6 w-6 mb-1" />
                    <span className="text-xs">Transfer.</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'fiado' ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod('fiado')}
                    className={`h-20 flex flex-col items-center justify-center border-orange-200 dark:border-orange-800 transition-colors ${
                      paymentMethod === 'fiado' 
                        ? 'bg-orange-600 text-white hover:bg-orange-700' 
                        : 'bg-orange-50 text-orange-900 hover:bg-orange-100 dark:bg-orange-950/30 dark:text-orange-100 dark:hover:bg-orange-900/50'
                    }`}
                    data-testid="payment-fiado-btn"
                  >
                    <HandCoins className="h-6 w-6 mb-1" />
                    <span className="text-xs">Crédito</span>
                  </Button>
                  <Button
                    variant={paymentMethod === 'multiple' ? 'default' : 'outline'}
                    onClick={openSplitModal}
                    className="h-20 flex flex-col items-center justify-center"
                    data-testid="payment-multiple-btn"
                  >
                    <CreditCard className="h-6 w-6 mb-1 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs">Mixto</span>
                  </Button>
              </div>

              <Button onClick={handleCheckout} disabled={cart.length === 0 || loading} className="w-full h-14 text-lg font-bold rounded-full shadow-lg shadow-indigo-500/20 hover:scale-105 active:scale-95 transition-transform" data-testid="checkout-btn">
                {loading ? 'Procesando...' : 'Completar Venta'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
        </>
      )}
    </div>
  );
}
